import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ArrowRight, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const navLinks = [
  { label: 'Product', to: '/product' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'About', to: '/about' },
  { label: 'Contact', to: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';
  const transparent = isHome && !scrolled;

  return (
    <header style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      background: transparent ? 'transparent' : 'rgba(255,255,255,0.95)',
      backdropFilter: transparent ? 'none' : 'blur(12px)',
      WebkitBackdropFilter: transparent ? 'none' : 'blur(12px)',
      borderBottom: transparent ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.06)',
      transition: 'all 0.3s ease',
    }}>
      <div style={{
        maxWidth: 1200,
        margin: '0 auto',
        padding: '0 24px',
        height: 72,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 48 }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center' }}>
            <img
              className="navbar-logo"
              src="/images/logo.png"
              alt="LeadNova"
              style={{
                height: 40,
                filter: transparent ? 'brightness(1.8)' : 'none',
                transition: 'all 0.3s',
              }}
            />
          </Link>

          <nav className="navbar-desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {navLinks.map((link) => {
              const isActive = location.pathname === link.to;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: isActive
                      ? (transparent ? '#fff' : 'var(--sky)')
                      : (transparent ? 'rgba(255,255,255,0.8)' : 'var(--gray-500)'),
                    textDecoration: 'none',
                    padding: '8px 16px',
                    borderRadius: 8,
                    transition: 'all 0.2s',
                    background: isActive
                      ? (transparent ? 'rgba(255,255,255,0.1)' : 'var(--sky-light)')
                      : 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      (e.target as HTMLElement).style.color = transparent ? '#fff' : 'var(--gray-900)';
                      (e.target as HTMLElement).style.background = transparent ? 'rgba(255,255,255,0.08)' : 'var(--gray-50)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      (e.target as HTMLElement).style.color = transparent ? 'rgba(255,255,255,0.8)' : 'var(--gray-500)';
                      (e.target as HTMLElement).style.background = 'transparent';
                    }
                  }}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="navbar-desktop-actions" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {isAuthenticated ? (
            <>
              <Link
                to="/app/dashboard"
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: transparent ? 'rgba(255,255,255,0.9)' : 'var(--gray-900)',
                  textDecoration: 'none',
                  padding: '8px 20px',
                  borderRadius: 8,
                  transition: 'all 0.2s',
                  border: transparent ? '1px solid rgba(255,255,255,0.2)' : '1px solid var(--gray-200)',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background = transparent ? 'rgba(255,255,255,0.1)' : 'var(--gray-50)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = 'transparent';
                }}
              >
                {user?.firstName || user?.fullName || 'Account'}
              </Link>
              <button
                onClick={handleLogout}
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: transparent ? 'rgba(255,255,255,0.9)' : 'var(--gray-700)',
                  padding: '8px 20px',
                  borderRadius: 8,
                  background: 'transparent',
                  border: transparent ? '1px solid rgba(255,255,255,0.2)' : '1px solid var(--gray-200)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  transition: 'all 0.2s',
                  fontFamily: 'inherit',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background = transparent ? 'rgba(255,255,255,0.1)' : 'var(--gray-50)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = 'transparent';
                }}
              >
                <LogOut size={15} />
                Log Out
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: transparent ? 'rgba(255,255,255,0.9)' : 'var(--gray-900)',
                  textDecoration: 'none',
                  padding: '8px 20px',
                  borderRadius: 8,
                  transition: 'all 0.2s',
                  border: transparent ? '1px solid rgba(255,255,255,0.2)' : '1px solid var(--gray-200)',
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLElement).style.background = transparent ? 'rgba(255,255,255,0.1)' : 'var(--gray-50)';
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLElement).style.background = 'transparent';
                }}
              >
                Log In
              </Link>
              <Link
                to="/pricing"
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#fff',
                  textDecoration: 'none',
                  padding: '8px 20px',
                  borderRadius: 8,
                  background: 'var(--green)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#2e9229'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--green)'; }}
              >
                Get Started Free
                <ArrowRight size={15} />
              </Link>
            </>
          )}
        </div>

        <button
          className="navbar-hamburger"
          style={{
            display: 'none',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 8,
            color: transparent ? '#fff' : 'var(--gray-900)',
          }}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          {menuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {menuOpen && (
        <div className="navbar-mobile-menu" style={{
          position: 'absolute',
          top: 72,
          left: 0,
          right: 0,
          background: '#fff',
          borderTop: '1px solid var(--gray-100)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
          padding: '12px 24px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          zIndex: 999,
        }}>
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              style={{
                fontSize: 15,
                fontWeight: 500,
                color: location.pathname === link.to ? 'var(--sky)' : 'var(--gray-900)',
                textDecoration: 'none',
                padding: '12px 12px',
                borderRadius: 8,
                background: location.pathname === link.to ? 'var(--sky-light)' : 'transparent',
              }}
            >
              {link.label}
            </Link>
          ))}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12, borderTop: '1px solid var(--gray-100)', paddingTop: 16 }}>
            {isAuthenticated ? (
              <>
                <Link to="/app/dashboard" style={{ display: 'flex', justifyContent: 'center', fontSize: 14, fontWeight: 600, color: 'var(--gray-900)', textDecoration: 'none', padding: '10px 20px', borderRadius: 8, border: '1px solid var(--gray-200)' }}>
                  {user?.firstName || user?.fullName || 'Dashboard'}
                </Link>
                <button onClick={handleLogout} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, color: 'var(--gray-700)', padding: '10px 20px', borderRadius: 8, border: '1px solid var(--gray-200)', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit' }}>
                  <LogOut size={15} />
                  Log Out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" style={{ display: 'flex', justifyContent: 'center', fontSize: 14, fontWeight: 500, color: 'var(--gray-900)', textDecoration: 'none', padding: '10px 20px', borderRadius: 8, border: '1px solid var(--gray-200)' }}>Log In</Link>
                <Link to="/pricing" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, color: '#fff', textDecoration: 'none', padding: '10px 20px', borderRadius: 8, background: 'var(--green)' }}>
                  Get Started Free
                  <ArrowRight size={15} />
                </Link>
              </>
            )}
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .navbar-desktop-nav { display: none !important; }
          .navbar-desktop-actions { display: none !important; }
          .navbar-hamburger { display: block !important; }
          .navbar-logo { height: 36px !important; }
        }
      `}</style>
    </header>
  );
}
